import { PropertyValueMap, TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property, query, queryAssignedNodes, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { SlotController } from '../../controllers/slot-controller';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import '../icon/icons/arrow-back';
import { BlocksListItem } from '../list-item/list-item';
import type { ListBehavior, ListOrientationType, ListSpacing, ListTriggerType, ShadowType } from './list.model';
import styles from './list.styles';

/**
 * @slot default - The list items
 * @slot back-button - Components back-button slot when the behavior of list items is drawer
 * @slot list-header - Components heading slot when the behavior of list items is drawer
 * @csspart base - The component's base wrapper. Use the token `--cat-list-body-height` to set the height of the list provided its 
   parent is list item with drawer behavior. To be used  with navigation components in mobile view
 * @csspart list - The component's list.
 * @csspart part-default - The component's default slot.
 * @csspart before - The component's before wrapper.
 * @csspart button - The component's back button.
 * @csspart part-back-button - The component's back button slot.
 * @csspart arrow-back - The component's arrow-back icon.
 * @csspart part-list-header - The components heading slot which comes with back button.
 */
export class BlocksList extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Spacing variants
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**padded** applies more padding in between list items compared to the default</li>
   * <li>**condensed** reduces padding in between list items compared to the default</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  spacing: ListSpacing = 'default';

  /**
   * Behavior
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**overflow** turns the list into an overflow list</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  behavior: ListBehavior;

  /**
   * This is a public state and got reset in primary-nav-item
   * @ignore
   */
  @state()
  isSteppedMegaMenu: boolean | undefined;

  /**
   * Orientation
   * <cat-text-passage size="sm">
   * <ul>
   * <li>default renders a stacked list</li>
   * <li>**horizontal** renders the list side-by-side and wraps unless overflow is turned on</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  orientation: ListOrientationType;

  /**
   * Shadow
   * <cat-text-passage size="sm">
   * <ul>
   * <li>By default **subtle**, changes the shadow to background subtle token to work on subtle bands of color
   * <li>**none** will removes the shadow</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  shadow: ShadowType = 'subtle';

  /**
   * When the `singleOpen` property is set to true, only one subtree can be open at a time. If a user opens a new subtree, any previously open subtree automatically closes.
   */
  @property({ type: Boolean })
  singleOpen = false;

  /**
   * role of list. Default - list, listitem
   * within search field role is - listbox
   * within primary-nav role is - menu
   * within utility-nav role is - menu
   * within contextual-menu role is - menu
   * flyout/expandable list set list role to - menu
   */
  @property({ type: String })
  listRole = 'list';

  /**
   * Can be used to enable click or hover, default is hover
   */
  @property({ type: String })
  trigger: ListTriggerType = 'hover';

  /**
   * Aria label attribute
   * - Provides an accessible name for the list element
   */
  @property({ type: String })
  arialabel: string | undefined;

  /**
   * Aria label attribute
   * - Provides an accessible name for the back button in drawer behavior
   */
  @property({ type: String })
  ariaLabelBackButton = 'back';

  /**
   * isStart state
   * 1) Controls the beginning gradient on the overflow list
   */
  @state()
  private isStart: boolean | undefined;

  /**
   * isEnd state
   * 1) Controls the ending gradient on the overflow list
   */
  @state()
  private isEnd: boolean | undefined;

  /**
   * Query the back button
   * @ignore
   */
  @query('.cat-c-list-header--back__button')
  public _catListBackButton: HTMLButtonElement | undefined;

  private get isLTR() {
    return document.dir === 'ltr' || document.dir === '';
  }

  /**
   * Query all the list items
   */
  @queryAssignedNodes({ flatten: false })
  private listItems: Array<BlocksListItem> | undefined;

  /**
   * Tracks the number of assigned elements in the slot to avoid redundant processing on slotchange.
   */
  private _lastSlotAssignedLength = 0;

  /**
   * Query all the list items in the list
   */
  private get _listItems(): Array<BlocksListItem> {
    return [...(this.querySelectorAll(':scope > cat-list-item') as any)];
  }

  /**
   * query the parent listitem - mainly comes into picture if the list item is of drawer or flyout variant
   */
  private get _parentListItem(): BlocksListItem | undefined | null {
    return this.closest('cat-list-item');
  }

  /**
   * check if the parent list item has behavior as drawer
   */
  private get isListItemDrawer(): boolean | undefined | null {
    return this._parentListItem?.isDrawerBehavior;
  }

  /**
   * check if it is the first panel, i.e, it does not have any list item as its parent
   */
  private get isFirstPanel(): boolean {
    return !this._parentListItem ? true : false;
  }

  protected slots = new SlotController(this, null, 'list-header');

  /**
   * Lifecycle method that is called after the component's DOM has been updated for the first time.
   *
   * This method performs the following actions:
   * - If the `behavior` property is set to `'overflow'` and the `shadow` property is set to `'subtle'`,
   *   it schedules the `setShadows` method to run after a short delay to ensure proper initialization.
   * - Adds a `resize` event listener to the `window` object to dynamically update shadows when the window is resized.
   *
   * Note: The `setShadows` method is expected to handle the logic for updating the component's shadow styling.
   */
  firstUpdated() {
    if (this.behavior === 'overflow' && this.shadow === 'subtle') {
      setTimeout(() => {
        this.setShadows(); /* 1 */
      }, 1);
      window.addEventListener('resize', this.setShadows);
    }
  }

  /**
   * Disconnected callback lifecycle
   * 1) Remove window resize event listener
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.setShadows);
  }

  /**
   * Updated lifecycle
   */
  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('spacing') || _changedProperties.has('trigger')) {
      this.setSizeAndTrigger();
    }
  }

  /**
   * On overflow list inner scroll
   * 1) On scroll, if position from left is greater than 0, set isStart to true. Otherwise set isStart to false.
   * 2) On scroll, If last child is fully in the viewport, set isEnd to false. Otherwise, set isEnd to true.
   */
  private setShadows = () => {
    this.setIsStart();
    this.setIsEnd();
  };

  /**
   * Set isStart state
   * 1) If position from left is greater than 0, set isStart to true. Otherwise set isStart to false.
   */
  private setIsStart(): void {
    const overflowListInner = this.shadowRoot?.querySelector('.cat-c-list--overflow');
    if (!overflowListInner) {
      return;
    }
    if (this.isLTR) {
      if (overflowListInner.scrollLeft > 0) {
        this.isStart = true; /* 1 */
      } else {
        this.isStart = false; /* 1 */
      }
    } else {
      if (overflowListInner.scrollLeft < 0) {
        this.isStart = true; /* 1 */
      } else {
        this.isStart = false; /* 1 */
      }
    }
  }

  /**
   * Set isEnd state
   * 1) If scroll reach right end, set isEnd to false. Otherwise set isEnd to true.
   */
  private setIsEnd(): void {
    const overflowListInner = this.shadowRoot?.querySelector('.cat-c-list--overflow');
    if (!overflowListInner) {
      return;
    }
    const currentScrollPos = overflowListInner.scrollLeft;
    const maxScrollWidth = overflowListInner.scrollWidth - overflowListInner.clientWidth - 1;
    if (this.isLTR) {
      if (Math.ceil(currentScrollPos) >= maxScrollWidth) {
        this.isEnd = false;
      } else {
        this.isEnd = true;
      }
    } else {
      if (Math.floor(currentScrollPos) <= -maxScrollWidth) {
        this.isEnd = false;
      } else {
        this.isEnd = true;
      }
    }
  }

  /**
   * Set sizing
   * 1) Loop through all the slotted list items
   * 2) Sets the list item size based on the list spacing property
   */
  private setSizeAndTrigger() {
    /* 1 */
    if (this.listItems) {
      this.listItems.forEach((item: BlocksListItem) => {
        /* 2 */
        item.trigger = this.trigger;
        if (this.spacing === 'padded') {
          item.size = 'lg';
          return;
        }
        if (this.spacing === 'condensed') {
          item.size = 'sm';
          return;
        }
        item.size = 'default';
      });
    }
  }

  /**
   * navigate to previous panel, when back button is pressed.
   * Executed only if the list panel is opened via a list-item with drawer behavior
   */
  private navigateToPreviousPanel = () => {
    if (this.isListItemDrawer && this._parentListItem && this._parentListItem.isActive) {
      this._parentListItem.isActive = false;
    }
  };

  /**
   * focus the back button when navigated to previous panel
   */
  private focusBackButton = (e: KeyboardEvent) => {
    if ([KEYS.ESCAPE, KEYS.ENTER, KEYS.SPACE].includes(e.key) && this._parentListItem) {
      e.preventDefault();
      e.stopPropagation();
      this._parentListItem.isActive = false;
      this._parentListItem.focus();
    }
  };
  /**
   * Set singleOpen
   * 1) Close all List Item except for the one that was clicked
   */
  private setSingleOpen = (event: Event) => {
    const item = event.target as BlocksListItem;

    if (this.singleOpen && this._listItems) {
      const listItems = this.shadowRoot?.querySelector<HTMLSlotElement>('slot:not([name])')?.assignedElements() as BlocksListItem[];
      listItems.forEach((blockItem: BlocksListItem) => {
        if (!item.disabled && !item.isStatic && !blockItem.contains(item)) {
          if(blockItem.hasItemsSlot && blockItem.isActive){
            blockItem.handleFlyoutClosed();
          }
          blockItem.isActive = false;
        }
      });
    }
  };

  private handleSlotChange(event: Event) {
    const slot = event.target as HTMLSlotElement;
    const assigned = slot.assignedElements({ flatten: true });
    if (assigned.length === this._lastSlotAssignedLength) {
      return; // Ignore redundant call
    }
    this._lastSlotAssignedLength = assigned.length;
    this.setSizeAndTrigger();
    this.dispatch({
      eventName: 'bl-list-slot-change'
    });
  }

  render(): TemplateResult<1> {
    const wrapperClassNames = this.componentClassNames('cat-c-list-overflow-wrapper', {
      'cat-is-overflow-left': this.isStart === true,
      'cat-is-overflow-right': this.isEnd === true,
      'cat-c-list-overflow-wrapper--shadow-subtle': this.shadow === 'subtle'
    });

    const componentClassName = this.componentClassNames('cat-c-list', {
      'cat-c-list--horizontal': this.orientation === 'horizontal',
      'cat-c-list--overflow': this.behavior === 'overflow',
      'cat-c-list--steppedmegamenu': this.isSteppedMegaMenu === true,
      'cat-c-list--has-list-header-slot': this.slots.hasSlotted('list-header'),
      'cat-c-list-has-list-item-drawer': !this.isFirstPanel && this.isListItemDrawer
    });

    if (this.behavior === 'overflow') {
      return html`
        <div part="base" class=${wrapperClassNames}>
          <ul
            part="list"
            aria-label=${ifDefined(this.arialabel)}
            .role=${this.listRole}
            class="${componentClassName}"
            @scroll=${this.shadow === 'subtle' ? this.setShadows : nothing}
          >
            <slot part="part-default" @slotchange=${this.handleSlotChange}></slot>
          </ul>
        </div>
      `;
    } else {
      return html`
        <ul part="base" aria-label=${ifDefined(this.arialabel)} .role=${this.listRole} class="${componentClassName}">
          <div part="before" class="cat-c-list--drawer-before" @click=${this.navigateToPreviousPanel} @keydown=${this.focusBackButton}>
            <button part="button" class="cat-c-list-header--back__button" aria-label=${this.ariaLabelBackButton}>
              <slot part="part-back-button" name="back-button"
                ><cat-icon-arrow-back exportparts=${this.getExportParts('arrow-back', 'icon')}></cat-icon-arrow-back
              ></slot>
            </button>
            <slot part="part-list-header" name="list-header"></slot>
          </div>
          <slot part="part-default" @slotchange=${this.handleSlotChange} @click=${this.singleOpen ? this.setSingleOpen : nothing}></slot>
        </ul>
      `;
    }
  }
}

if (customElements.get('cat-list') === undefined) {
  customElements.define('cat-list', BlocksList);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-list': BlocksList;
  }
}
